/**
 * 課題文プレビュー本体: 学習目標カード（summary+カテゴリピル）+ タスクリスト
 * scenario-preview の課題文タブと管理画面の課題文プレビューで共有。
 * フォント・余白は scenario-preview と同じデザイントークン。
 */
import type { InterviewProgress } from "../lib/interview-task-progress";
import {
  InstructionProgressCard,
  type ProgressKind,
  TaskProgressBadge,
  TaskProgressDots,
} from "./InterviewProgressGauge";

const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
};

export interface InstructionPreviewTask {
  no?: number;
  title?: string;
  speech?: string;
  details?: string[];
}

export interface InstructionPreviewBodyProps {
  summary?: string | null;
  targetCategories: string[];
  tasks: InstructionPreviewTask[];
  /**
   * categoryId → 表示名のマップ。
   * サーバーが condition_data.categoryDefinitions[].displayName から解決した
   * 最新の表示名を渡すと、古い固定カテゴリ名（旧 intake ルーブリック）を上書きできる。
   * 渡されなかった場合は ConditionCategoryTable の固定マップにフォールバックする。
   */
  categoryLabels?: Record<string, string>;
  /** 外側 container の maxWidth（デフォルト 900） */
  maxWidth?: number;
  /** 面接中の聞き取り状況。渡すと進み具合カードとタスクごとのバッジを出す。 */
  progress?: InterviewProgress | null;
  /** 進み具合が患者面接の聞き取りか、報告か（見出しと案内の文言が変わる）。 */
  progressKind?: ProgressKind;
}

export function resolveCategoryLabel(
  categoryId: string,
  labels?: Record<string, string>,
): string {
  if (labels) {
    const direct = labels[categoryId];
    if (direct) return direct;
    const base = categoryId.split("-")[0];
    const baseLabel = labels[base];
    if (baseLabel) return baseLabel;
  }
  // 番号の意味は CSV の版で変わるため、旧ルーブリックの名前で補わず番号のまま出す
  return categoryId;
}

export function InstructionPreviewBody({
  summary,
  targetCategories,
  tasks,
  categoryLabels,
  maxWidth = 900,
  progress,
  progressKind = "interview",
}: InstructionPreviewBodyProps) {
  if (progress) {
    return (
      <InterviewInstructionBody
        summary={summary}
        targetCategories={targetCategories}
        tasks={tasks}
        categoryLabels={categoryLabels}
        maxWidth={maxWidth}
        progress={progress}
        progressKind={progressKind}
      />
    );
  }
  return (
    <div
      style={{
        maxWidth,
        margin: "0 auto",
        fontFamily:
          '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        color: COLORS.ink,
      }}
    >
      {/* Intro card */}
      <div
        style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.line}`,
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: COLORS.muted,
            fontWeight: 700,
            letterSpacing: 1.5,
            marginBottom: 6,
          }}
        >
          このシナリオの学習目標
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.6 }}>
          {summary && summary.trim() ? (
            summary
          ) : (
            <span style={{ color: COLORS.muted, fontWeight: 500 }}>
              （シナリオ概要は未設定）
            </span>
          )}
        </div>
        {targetCategories.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 12 }}>
            {targetCategories.map((cat) => (
              <span
                key={cat}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 999,
                  background: COLORS.bg,
                  color: COLORS.muted,
                  fontWeight: 600,
                  border: `1px solid ${COLORS.line}`,
                }}
              >
                {resolveCategoryLabel(cat, categoryLabels)}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Task list */}
      <div
        style={{
          background: COLORS.surface,
          border: `1px solid ${COLORS.line}`,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 24px",
            borderBottom: `1px solid ${COLORS.line}`,
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: COLORS.bg,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 800 }}>
            練習で達成すべきタスク
          </div>
          <span style={{ fontSize: 11, color: COLORS.muted }}>
            全{tasks.length}項目
          </span>
        </div>
        {tasks.length === 0 ? (
          <div style={{ padding: "20px 24px", fontSize: 13, color: COLORS.muted }}>
            タスクが設定されていません
          </div>
        ) : (
          tasks.map((t, i) => {
            const speech = t.speech?.trim();
            return (
              <div
                key={i}
                style={{
                  padding: "16px 24px",
                  borderBottom:
                    i < tasks.length - 1
                      ? `1px solid ${COLORS.line}`
                      : "none",
                  display: "flex",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 14,
                    flexShrink: 0,
                    background: COLORS.ink,
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    fontSize: 12,
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 6 }}
                  >
                    {t.title || ""}
                  </div>
                  {speech && (
                    <p
                      style={{
                        margin: "8px 0 0",
                        color: "#3F3F46",
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.7,
                      }}
                    >
                      {speech}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * 面接中の課題文（#23 のモック）: 進み具合カード → タスクごとのカード → 学習目標。
 * 各タスクに達成バッジと、聞けた項目の点を付ける。
 */
function InterviewInstructionBody({
  summary,
  targetCategories,
  tasks,
  categoryLabels,
  maxWidth,
  progress,
  progressKind,
}: Required<Pick<InstructionPreviewBodyProps, "targetCategories" | "tasks" | "maxWidth" | "progressKind">> &
  Pick<InstructionPreviewBodyProps, "summary" | "categoryLabels"> & { progress: InterviewProgress }) {
  const taskProgress = progress.mode === "tasks" ? progress.tasks : [];
  return (
    <div
      style={{
        maxWidth,
        margin: "0 auto",
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        color: COLORS.ink,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <InstructionProgressCard progress={progress} kind={progressKind} />

      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.15em",
          color: COLORS.muted,
          padding: "2px 2px 0",
        }}
      >
        練習で達成すべきタスク
      </div>

      {tasks.length === 0 ? (
        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            padding: 14,
            fontSize: 13,
            color: COLORS.muted,
          }}
        >
          タスクが設定されていません
        </div>
      ) : (
        tasks.map((t, i) => {
          const item = taskProgress[i]?.total ? taskProgress[i] : null;
          const body = t.speech?.trim() || (t.details ?? []).filter(Boolean).join("。");
          return (
            <div
              key={i}
              data-testid="interview-instruction-task"
              style={{
                background: COLORS.surface,
                border: `1px solid ${item?.done ? "#86EFAC" : COLORS.line}`,
                borderRadius: 12,
                padding: 14,
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: 999,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 13,
                  fontWeight: 800,
                  background: item?.done ? "#15803D" : COLORS.ink,
                  color: "#FFFFFF",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t.no ?? i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.4 }}>{t.title || ""}</div>
                  {item && <TaskProgressBadge task={item} />}
                </div>
                {body && (
                  <div style={{ fontSize: 12.5, color: "#52525B", lineHeight: 1.6 }}>{body}</div>
                )}
                {item && <TaskProgressDots task={item} />}
              </div>
            </div>
          );
        })
      )}

      {(summary?.trim() || targetCategories.length > 0) && (
        <div
          style={{
            background: COLORS.surface,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 12,
            padding: 14,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.15em",
              color: COLORS.muted,
              marginBottom: 6,
            }}
          >
            このシナリオの学習目標
          </div>
          {summary?.trim() && (
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.6 }}>{summary}</div>
          )}
          {targetCategories.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 10 }}>
              {targetCategories.map((cat) => (
                <span
                  key={cat}
                  style={{
                    fontSize: 11,
                    padding: "3px 10px",
                    borderRadius: 999,
                    background: COLORS.bg,
                    color: COLORS.muted,
                    fontWeight: 600,
                    border: `1px solid ${COLORS.line}`,
                  }}
                >
                  {resolveCategoryLabel(cat, categoryLabels)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
